import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// SAM-175 freeze: new files may not import baseui directly — use the components/ui facade.
import { toaster } from '../components/ui/toast';
import { BOOKING_HAS_PAYMENT_MESSAGE, getApiErrorMessage, isBookingHasPayment } from '../utils/apiErrors';
import {
  BookingOpsUnavailableError,
  approveCancelRequest,
  cancelBookingOnBehalf,
  getBookingCancellationPolicy,
  getBookingHistory,
  getBookingReceipt,
  getCancelRequests,
  markNoShow,
  refundBooking,
  rejectCancelRequest,
  reissueBookingAccess,
  resendBookingReceipt,
  revokeBookingAccess,
  voidBooking,
  type BookingReceipt,
} from '../api/bookingOps';

/**
 * True when `error` is this route's "not deployed to this environment yet" signal (404/501 from
 * the parallel sam-lambda-functions PR). Every SAM-553 hook below exposes it through
 * `isUnavailable` instead of letting the query's own `error` bubble as a generic failure, so a
 * panel can render "not available yet" in place of the control/tab rather than crash or show a
 * scary toast for something that is simply not live yet.
 */
export function isBookingOpsUnavailable(error: unknown): boolean {
  return error instanceof BookingOpsUnavailableError;
}

// ---------------------------------------------------------------------------
// Changelog
// ---------------------------------------------------------------------------

export const useBookingHistory = (bookingId: string | null) => {
  return useQuery({
    queryKey: ['booking-history', bookingId],
    queryFn: () => getBookingHistory(bookingId as string),
    enabled: !!bookingId,
    retry: false,
  });
};

// ---------------------------------------------------------------------------
// Cancellation
// ---------------------------------------------------------------------------

export const useBookingCancellationPolicy = (bookingId: string | null, enabled: boolean) => {
  return useQuery({
    queryKey: ['booking-cancellation-policy', bookingId],
    queryFn: () => getBookingCancellationPolicy(bookingId as string),
    enabled: !!bookingId && enabled,
    retry: false,
  });
};

export const useCancelBookingOnBehalf = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId, reason }: { bookingId: string; reason: string }) =>
      cancelBookingOnBehalf(bookingId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['booking-history'] });
      toaster.positive('Booking cancelled', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(isBookingHasPayment(error) ? BOOKING_HAS_PAYMENT_MESSAGE : getApiErrorMessage(error, 'Failed to cancel booking'), { autoHideDuration: isBookingHasPayment(error) ? 6000 : 4000 });
    },
  });
};

export const useCancelRequests = (businessId: string | null) => {
  return useQuery({
    queryKey: ['booking-cancel-requests', businessId],
    queryFn: () => getCancelRequests(businessId as string),
    enabled: !!businessId,
    retry: false,
  });
};

export const useApproveCancelRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId, reason }: { bookingId: string; reason: string }) =>
      approveCancelRequest(bookingId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-cancel-requests'] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      toaster.positive('Cancellation request approved', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(getApiErrorMessage(error, 'Failed to approve request'), { autoHideDuration: 4000 });
    },
  });
};

export const useRejectCancelRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId, reason }: { bookingId: string; reason: string }) =>
      rejectCancelRequest(bookingId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-cancel-requests'] });
      toaster.positive('Cancellation request rejected', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(getApiErrorMessage(error, 'Failed to reject request'), { autoHideDuration: 4000 });
    },
  });
};

// ---------------------------------------------------------------------------
// Void / refund
// ---------------------------------------------------------------------------

export const useVoidBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId, reason }: { bookingId: string; reason: string }) =>
      voidBooking(bookingId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['booking-history'] });
      queryClient.invalidateQueries({ queryKey: ['booking-receipt'] });
      toaster.positive('Authorization voided', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(getApiErrorMessage(error, 'Failed to void authorization'), { autoHideDuration: 4000 });
    },
  });
};

export const useRefundBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId, reason, amount }: { bookingId: string; reason: string; amount?: number }) =>
      refundBooking(bookingId, reason, amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['booking-history'] });
      queryClient.invalidateQueries({ queryKey: ['booking-receipt'] });
      toaster.positive('Refund issued', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(getApiErrorMessage(error, 'Failed to issue refund'), { autoHideDuration: 4000 });
    },
  });
};

// ---------------------------------------------------------------------------
// No-show
// ---------------------------------------------------------------------------

export const useMarkBookingNoShow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId, reason }: { bookingId: string; reason?: string }) =>
      markNoShow(bookingId, reason),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['booking-history'] });
      toaster.positive(
        result.captured ? 'Guest marked as no-show — held card was captured' : 'Guest marked as no-show',
        { autoHideDuration: 4000 },
      );
    },
    onError: (error: any) => {
      toaster.negative(getApiErrorMessage(error, 'Failed to mark no-show'), { autoHideDuration: 4000 });
    },
  });
};

// ---------------------------------------------------------------------------
// Receipt
// ---------------------------------------------------------------------------

export const useBookingReceipt = (bookingId: string | null) => {
  return useQuery<BookingReceipt>({
    queryKey: ['booking-receipt', bookingId],
    queryFn: () => getBookingReceipt(bookingId as string),
    enabled: !!bookingId,
    retry: false,
  });
};

export const useResendBookingReceipt = () => {
  return useMutation({
    mutationFn: (bookingId: string) => resendBookingReceipt(bookingId),
    onSuccess: () => {
      toaster.positive('Receipt resent to the guest', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(getApiErrorMessage(error, 'Failed to resend receipt'), { autoHideDuration: 4000 });
    },
  });
};

// ---------------------------------------------------------------------------
// QR / access
// ---------------------------------------------------------------------------

export const useReissueBookingAccess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bookingId: string) => reissueBookingAccess(bookingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-history'] });
      toaster.positive('QR code reissued', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(getApiErrorMessage(error, 'Failed to reissue QR code'), { autoHideDuration: 4000 });
    },
  });
};

export const useRevokeBookingAccess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bookingId: string) => revokeBookingAccess(bookingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-history'] });
      toaster.positive('QR code invalidated', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(getApiErrorMessage(error, 'Failed to invalidate QR code'), { autoHideDuration: 4000 });
    },
  });
};
